import { useMutation, useQuery } from '@tanstack/react-query'
import * as api from '../data/api'
import type { BookingDraft, VehicleQuery, VehicleSort } from '../data/api'

/* One hook per thing a surface asks the API for, and one place where cache
   keys are written.

   The catalogue is read constantly and changes rarely, so rows stay fresh
   for five minutes: moving between the home page, the listing and a detail
   page then costs no requests at all, and the back button is instant. Every
   key starts with 'vehicles' so the whole catalogue can be invalidated with
   a single prefix the day the console can edit stock. */

const FRESH_FOR = 5 * 60_000

export const keys = {
  vehicles: (query: VehicleQuery, sort: VehicleSort) =>
    ['vehicles', 'list', query, sort] as const,
  vehicle: (id: string) => ['vehicles', 'detail', id] as const,
  featured: (limit: number) => ['vehicles', 'featured', limit] as const,
  newArrivals: (limit: number) => ['vehicles', 'new-arrivals', limit] as const,
  similar: (id: string, limit: number) => ['vehicles', 'similar', id, limit] as const,
  facets: (brands: string[] = []) => ['vehicles', 'facets', brands] as const,
  companies: () => ['companies', 'list'] as const,
}

export function useVehiclesQuery(
  query: VehicleQuery = {},
  sort: VehicleSort = 'featured',
  enabled = true,
) {
  return useQuery({
    queryKey: keys.vehicles(query, sort),
    queryFn: () => api.getVehicles(query, sort),
    staleTime: FRESH_FOR,
    enabled,
    /* A filter change keeps the previous rows on screen while the next set
       loads, rather than collapsing the grid into skeletons and jumping the
       page under the visitor's cursor. */
    placeholderData: (previous) => previous,
  })
}

export function useVehicleQuery(id: string) {
  return useQuery({
    queryKey: keys.vehicle(id),
    queryFn: () => api.getVehicleById(id),
    staleTime: FRESH_FOR,
    enabled: Boolean(id),
    /* A 404 is the answer, not a failure worth three more attempts. */
    retry: (count, error) => count < 2 && !isNotFound(error),
  })
}

export const useFeaturedQuery = (limit = 6) =>
  useQuery({
    queryKey: keys.featured(limit),
    queryFn: () => api.getFeatured(limit),
    staleTime: FRESH_FOR,
  })

export const useNewArrivalsQuery = (limit = 8) =>
  useQuery({
    queryKey: keys.newArrivals(limit),
    queryFn: () => api.getNewArrivals(limit),
    staleTime: FRESH_FOR,
  })

export const useSimilarQuery = (id: string, limit = 4) =>
  useQuery({
    queryKey: keys.similar(id, limit),
    queryFn: () => api.getSimilar(id, limit),
    staleTime: FRESH_FOR,
    enabled: Boolean(id),
  })

/** The filter panel's options. The chosen brands are part of the key as well
    as the request: they narrow the model list, so two different sets of makes
    are two different answers and must not share a cache entry. */
export const useFacetsQuery = (brands: string[] = []) =>
  useQuery({
    queryKey: keys.facets(brands),
    queryFn: () => api.getFacets(brands),
    staleTime: FRESH_FOR,
    /* Ticking a brand re-asks for the models under it. Keeping the last
       answer on screen meanwhile stops the whole panel collapsing to empty
       lists and jumping under the cursor that just ticked the box. */
    placeholderData: (previous) => previous,
  })

/** The makes the showroom carries, badges and all. Named once in the console
    and read everywhere, so it changes about as often as the sign outside —
    the catalogue's five minutes suit it. */
export const useCompaniesQuery = () =>
  useQuery({
    queryKey: keys.companies(),
    queryFn: () => api.getCompanies(),
    staleTime: FRESH_FOR,
  })

/* ---------- bookings ---------- */

/** Files a booking. Deliberately never retried: a request that timed out
    may well have been accepted, and a duplicate booking against a real
    payment is worse than an error the customer can act on. */
export const useCreateBooking = () =>
  useMutation({
    mutationFn: (draft: BookingDraft) => api.createBooking(draft),
    retry: false,
  })

function isNotFound(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  )
}
